import sessionStorage from '@avalara/bat-apps-shared/helpers/sessionStorage';
import { t } from '@avalara/bat-apps-shared/i18n';
import { SAlert, SBox, SBreadcrumbs, SCol, SContainer, SLoader, SRow } from '@avalara/skylab-sdk/react';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import conf from '../../../conf';
import { useAppDispatch } from '../../../hooks/use-app-dispatch';
import { pollForObligationProvisioning } from '../../../lib/side-effects/polling';
import { selectState } from '../../../selectors';
import { appendQueryParamToUrl } from '../../helpers/redirect';
import { ProvisioningSuccessAlert } from '../styled/order-confirmation';

import type { TranslationKey } from '@avalara/bat-apps-shared/i18n';

// Storage reads can throw the same way the writes in purchaseOffer/createAccountWithOffer can
// (e.g. SecurityError when third-party storage is blocked in an embedded/iframe view) — treat that
// the same as "not confirmed" (redirect to the bare offer page) rather than crashing the page.
const safeGetPurchaseFlag = (key: string): string | null => {
  try {
    return sessionStorage.get(key);
  } catch {
    return null;
  }
};

// Post-purchase page for offers reached through the Product Catalog / PDP flow — rendered by
// OfferContainer at /${offerFlow}/thank-you in place of the classic ThankYouPage for any offer
// whose `pages` include Pages.OrderReview (the same offers Order Review itself serves).
const OrderConfirmation = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const {
    account: { accountId },
    offer: { offerFlow, offerName, orderConfirmationContent, obligationProvisioningStatus, orderReviewBreadcrumbs },
  } = useSelector(selectState);

  // This route is reachable directly (bookmark, back button, typed URL) purely from static offer
  // metadata (pages.includes(Pages.OrderReview)) — nothing about the route itself proves a purchase
  // actually completed. purchaseOffer/createAccountWithOffer set this offer- and account-scoped
  // flag on success; checking both (rather than just offerFlow, or the pre-existing session-wide
  // `hasPurchasedOffer` that stays true for every offer/account once any purchase succeeds)
  // prevents an abandoned checkout, a *different* offer's purchase, and — since sessionStorage
  // survives logout/login and account switching within the same tab — a *different account's*
  // completed purchase from ever showing this offer as falsely confirmed.
  const hasPurchasedOffer =
    !!offerFlow && !!accountId && safeGetPurchaseFlag(`hasPurchasedOffer-${offerFlow}-${accountId}`) === 'true';

  useEffect(() => {
    // Without offerFlow there's no valid offer URL to land on — OfferContainer itself already
    // guarantees offerFlow is populated before any route (including this one) can render, but guard
    // here too rather than ever redirecting to a broken `/undefined`.
    if (!offerFlow || hasPurchasedOffer) return;
    history.replace(`/${offerFlow}`);
  }, [hasPurchasedOffer, offerFlow, history]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const provisioningStatusCheck = orderConfirmationContent?.provisioningStatusCheck;

  useEffect(() => {
    if (!provisioningStatusCheck) return undefined;

    const exitController = new AbortController();
    dispatch(pollForObligationProvisioning(`${conf.API_URL}${provisioningStatusCheck.pollUrl}`, exitController.signal));

    return () => exitController.abort();
  }, [provisioningStatusCheck]);

  const [firstSupplementalLine, secondSupplementalLine] = orderConfirmationContent?.supplementalText ?? [];
  const onboardingUrl = orderConfirmationContent?.onboardingUrl;
  const onboardingButtonText =
    orderConfirmationContent?.onboardingButtonText ?? t('client.order-confirmation.go-to-onboarding');
  const isProvisioned = obligationProvisioningStatus === 'success';
  const hasProvisioningFailed = obligationProvisioningStatus === 'failed';

  const handleBreadcrumbNav = (_event: Event, targetHref: string) => history.push(targetHref);

  if (!hasPurchasedOffer) return null;

  // Reuses Order Review's own breadcrumb config, since Confirmation extends the same trail one
  // step further. Order Review's last crumb (e.g. "Checkout") has no url there — it's the current
  // page — but from here it's a real, clickable step back, so it's given the order-review route
  // for this offer. Its own url must include the /buy basename for the same reason as Order
  // Review's config: SBreadcrumbs hands navHandler the raw href and does NOT preventDefault when a
  // handler is present, so a real browser navigation happens too.
  const breadcrumbs = orderReviewBreadcrumbs?.length
    ? [
        ...orderReviewBreadcrumbs.map((item, index) =>
          index === orderReviewBreadcrumbs.length - 1 && !item.url
            ? { ...item, url: `/buy/${offerFlow}/order-review` }
            : item,
        ),
        { labelKey: 'client.order-confirmation.breadcrumb.confirmation' },
      ]
    : undefined;

  return (
    <SContainer data-ref='buy.order-confirmation.page' className='pad-top-xl'>
      {breadcrumbs && breadcrumbs.length > 0 && (
        <SBreadcrumbs
          navItems={breadcrumbs.map(({ labelKey, url }) => ({
            label: t(labelKey as TranslationKey),
            url: url ? appendQueryParamToUrl(url, location.search.slice(1)) : url,
            emitNavEvent: !!url,
          }))}
          navHandler={handleBreadcrumbNav}
          data-ref='buy.order-confirmation.breadcrumb'
        />
      )}

      <SRow className='justify-content-center'>
        <SCol span='8' className='flex flex-dir-col align-items-center text-center'>
          <img
            className='pad-bottom-md'
            width={200}
            src={`${conf.ASSET_PATH}/assets/images/avi_main.svg`}
            alt={t('client.accessibility.order-confirmation-avi-image')}
            data-ref='buy.order-confirmation.avi-image'
          />
          <h1 className='pad-bottom-md' data-ref='buy.order-confirmation.heading'>
            {t('client.order-confirmation.heading', { offerName })} 🎉
          </h1>
          {firstSupplementalLine && (
            <p className='pad-bottom-sm' data-ref='buy.order-confirmation.supplemental-text-1'>
              {firstSupplementalLine}
            </p>
          )}
          {secondSupplementalLine && (
            <p className='pad-bottom-lg' data-ref='buy.order-confirmation.supplemental-text-2'>
              {secondSupplementalLine}
            </p>
          )}
          {provisioningStatusCheck && !isProvisioned && !hasProvisioningFailed && (
            <SBox
              className='pad-all-md margin-bottom-lg text-left'
              data-ref='buy.order-confirmation.provisioning-status'
            >
              <SRow className='align-items-center'>
                <SCol span='2' className='flex justify-content-center'>
                  <SLoader loading />
                </SCol>
                <SCol span='10'>
                  <p className='text-strong margin-bottom-xs'>{provisioningStatusCheck.activatingTitle}</p>
                  <p>{provisioningStatusCheck.activatingBody}</p>
                </SCol>
              </SRow>
            </SBox>
          )}
          {provisioningStatusCheck && isProvisioned && (
            <ProvisioningSuccessAlert>
              <SAlert
                className='show-icon margin-bottom-lg'
                status='success'
                noDismiss
                data-ref='buy.order-confirmation.provisioning-success'
              >
                {provisioningStatusCheck.readyBody}
              </SAlert>
            </ProvisioningSuccessAlert>
          )}
          {provisioningStatusCheck && hasProvisioningFailed && (
            <SAlert
              className='show-icon margin-bottom-lg'
              status='error'
              noDismiss
              data-ref='buy.order-confirmation.provisioning-error'
            >
              {t('client.order-confirmation.provisioning-error')}
            </SAlert>
          )}
          <div className='flex'>
            {onboardingUrl && provisioningStatusCheck && (
              <button
                type='button'
                className='button primary margin-right-md'
                disabled={!isProvisioned}
                onClick={() => {
                  window.location.href = onboardingUrl;
                }}
                data-ref='buy.order-confirmation.go-to-onboarding'
              >
                {onboardingButtonText}
              </button>
            )}
            {onboardingUrl && !provisioningStatusCheck && (
              <a
                className='button primary margin-right-md'
                href={onboardingUrl}
                data-ref='buy.order-confirmation.go-to-onboarding'
              >
                {onboardingButtonText}
              </a>
            )}
            <a
              className={`button ${onboardingUrl ? 'secondary' : 'primary'}`}
              href={conf.HOME_URL}
              data-ref='buy.order-confirmation.go-to-home'
            >
              {t('client.order-confirmation.go-to-home')}
            </a>
          </div>
        </SCol>
      </SRow>
    </SContainer>
  );
};

export default OrderConfirmation;
